import { RequiredValueEmptyError } from '@/ui/error';
import { commify, debounce } from '@/ui/util';
import {
  concat,
  each,
  entries,
  filter,
  isNil,
  isNumber,
  join,
  map,
  pipe,
  reduce,
  toArray,
  zipWithIndex,
} from '@fxts/core';
import { add, differenceInMilliseconds } from 'date-fns';
import { $, CustomEventWithDetail, html, View } from 'rune-ts';
import { Icon } from '../Icon/Icon';
import { Typo } from '../Typo/Typo';
import klass from './Input.module.scss';

export interface InputData {
  value?: string;
  id?: string;
}

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
 * */
export interface InputBaseOption {
  /**
   * @default 'text'
   * */
  name?: string;
  type?: 'text' | 'password' | 'email' | 'tel' | 'url' | 'number';
  autocapitalize?: boolean;
  autocomplete?: string;
  disabled?: boolean;
  max?: number;
  min?: number;
  maxlength?: number;
  minlength?: number;
  placeholder?: string;
  required?: boolean;
  readonly?: boolean;
  autofocus?: boolean;
  step?: number;
  pattern?: string;
  /**
   * @see https://developer.mozilla.org/ko/docs/Web/HTML/Global_attributes/inputmode#none
   */
  inputmode?: 'text' | 'none' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
}

export interface InputControllerOption {
  /**
   * @default input type이 password면 'password'로 강제됩니다. 아니면 'default'가 기본 값입니다.
   * */
  type?: 'default' | 'password' | 'timer';
  /**
   * @description type이 'timer'인 경우에만 기본 값이 설정됩니다.
   * */
  timer?: {
    /**
     * @default 5
     * */
    minutes: number;
    seconds?: number;
    /**
     * @default false
     * */
    autoplay: boolean;
  };
}

export interface InputValidatorOption {
  /**
   * @description
   * controller type이 timer인 경우 첫 번째 message만 사용됩니다.
   * */
  message: string;
  /**
   * 해당 함수가 없는 경우 항상 메시지가 비활성화됩니다.
   * @description false: error, true: not error \
   * 해당 함수를 넣은 데이터로 직접 hydrate 시도 시 에러가 발생할 수 있습니다.
   * 해당 합수 옵션은 반드시 한 번 View로 감싼 다음 SSR을 시도해야 합니다.\
   * controller type이 timer인 경우 무시됩니다.
   * */
  validator?: (value: string) => boolean;
}

export interface InputHelperOption {
  /**
   * @description
   * default = value가 없는 경우 에러를 표시 하지 않습니다.\
   * all = value가 없어도 에러를 표시합니다.\
   * check = validators가 하나여도 체크표시가 나타납니다.\
   * always = 회색 메시지가 나타납니다.
   * */
  type?: 'default' | 'all' | 'check' | 'always';
  validators?: InputValidatorOption[];
}

export interface InputOption {
  attr?: InputBaseOption;
  controller?: InputControllerOption;
  helper?: InputHelperOption;

  commify?: boolean;
  delete_pattern?: RegExp;
  klass?: string;
}

export class InputTimerEndEvent extends CustomEvent<never> {}
export class InputTimerStartEvent extends CustomEvent<never> {}
export class InputChangeEvent extends CustomEventWithDetail<Input> {}

class InputBase extends View<InputData> {
  constructor(
    data: InputData,
    public option: InputBaseOption,
  ) {
    super(data);
  }

  override template() {
    return html`
      <input
        ${this.data.id ? `id=${this.data.id}` : ''}
        class="${klass.input_base} ${Typo.typo.p1}"
        value="${this.data.value ?? ''}"
        ${this.makeInputBaseAttrHtml()}
      />
    `;
  }

  reset() {
    const input$ = this.element() as HTMLInputElement;
    input$.value = '';
    this.data.value = '';
  }

  getValue() {
    const input$ = this.element() as HTMLInputElement;
    this.data.value = input$.value;
    return input$.value;
  }

  setValue(val: string) {
    const input$ = this.element() as HTMLInputElement;
    input$.value = val;
    this.data.value = input$.value;
  }

  makeInputBaseAttr() {
    return pipe(
      { ...this.option, has_value: (!!this.data.value).toString() },
      entries,
      filter(([_, value]) => (isNumber(value) ? value > 0 : !isNil(value))),
    );
  }

  makeInputBaseAttrHtml(prefix?: string) {
    return html`${pipe(
      this.makeInputBaseAttr(),
      map(([k, v]) => {
        if (!v) return '';
        return typeof v === 'boolean'
          ? html`${prefix ? prefix + '-' : ''}${k}="${v.toString()}"`
          : html`${prefix ? prefix + '-' : ''}${k}="${html.preventEscape(v?.toString() ?? '')}"`;
      }),
      reduce((a, b) => html`${a} ${b}`),
    )}`;
  }

  override redraw() {
    const input$ = $(this.element());

    pipe(
      this.makeInputBaseAttr(),
      each(([k, v]) => {
        if (!v) {
          input$.removeAttribute(k);
        } else {
          input$.setAttribute(k, v ? v : '');
        }
      }),
    );

    return this;
  }
}

class InputController extends View<InputData> {
  private timer?: ReturnType<typeof setInterval>;
  private time?: Date;
  private is_end = false;
  public password_visible = false;
  public button_klass = {
    reset: klass.reset,
    visible: klass.visible_toggle,
  };

  constructor(
    data: InputData,
    public option: InputControllerOption = {},
  ) {
    super(data);

    option.type = option.type ?? 'default';

    if (option.type === 'timer') {
      option.timer = option.timer ?? { minutes: 5, autoplay: false };
    }
  }

  override template() {
    if (this.option.type === 'timer') {
      if (!this.option.timer) throw new RequiredValueEmptyError(this, 'data.timer');

      const time_text = this.is_end
        ? '00:00'
        : `${this.option.timer.minutes < 10 ? `0${this.option.timer.minutes}` : this.option.timer.minutes}:00`;
      return html` <div class="${klass.input_controller}" data-end="${this.is_end}">
        ${new Typo({ text: time_text }, { typo: 'p3' })}
      </div>`;
    }

    if (this.option.type === 'password') {
      return html` <div class="${klass.input_controller}">
        <button tabindex="-1" type="button" class="${this.button_klass.reset} ${klass.button_container}">
          ${new Icon({ name: 'remove', size: 16 })}
        </button>
        <button tabindex="-1" type="button" class="${this.button_klass.visible} ${klass.button_container}">
          ${new Icon({ name: !this.password_visible ? 'visible' : 'hide', size: 16 })}
        </button>
      </div>`;
    }

    return html` <div class="${klass.input_controller}">
      <button tabindex="-1" type="button" class="${this.button_klass.reset} ${klass.button_container}">
        ${new Icon({ name: 'remove', size: 16 })}
      </button>
    </div>`;
  }

  override onRender() {
    if (this.option.timer?.autoplay && !this.timer) {
      this.start();
    }
  }

  override onMount() {
    if (this.option.timer?.autoplay && !this.timer) {
      this.start();
    }
  }

  override onUnmount() {
    this.stop();
  }

  override redraw() {
    if (this.option.type === 'timer') {
      this.element().dataset.end = this.is_end.toString();
      return this;
    } else {
      (this.subViewsFromTemplate as Icon[]).forEach((view) => {
        if (view.data.name === 'visible' || view.data.name === 'hide') {
          view.data.name = !this.password_visible ? 'visible' : 'hide';
        }

        view.redraw();
      });
      return this;
    }
  }

  timerValidator(message?: string): InputValidatorOption {
    if (!message) throw new RequiredValueEmptyError(this, 'option.helper.validators[0].message');

    return {
      message,
      validator: () => !this.is_end,
    };
  }

  start() {
    if (!this.option.timer) throw new RequiredValueEmptyError(this, 'data.timer');

    this.is_end = false;
    this.dispatchEvent(InputTimerStartEvent, { bubbles: true });
    this.time = add(new Date(), this.option.timer);
    this.timerHandler();
    this.timer = setInterval(() => {
      this.timerHandler();
    }, 1000);
  }

  stop() {
    clearInterval(this.timer);
  }

  private timerHandler() {
    if (!this.time) throw new RequiredValueEmptyError(this, 'time');

    const seconds = Math.round(differenceInMilliseconds(this.time, new Date()) / 1000);

    const text = this.subView(Typo);

    if (!text) throw new RequiredValueEmptyError(this, 'this.subView(Typo)');

    if (seconds <= 0) {
      text.data.text = '00:00';
      text.redraw();
      this.is_end = true;
      this.stop();
      this.dispatchEvent(InputTimerEndEvent, { bubbles: true });
    } else {
      const minute = Math.floor(seconds / 60);
      text.data.text =
        (minute < 10 ? `0${minute}` : minute) + ':' + (seconds % 60 < 10 ? '0' + (seconds % 60) : seconds % 60);
      text.redraw();
    }
  }

  public restart() {
    this.stop();
    this.start();
  }
}

class InputHelper extends View<InputData> {
  private validators_status: (boolean | null)[] = [];
  private validator?: InputValidatorOption;
  public state = {
    enable: false,
    valid: true,
  };

  constructor(
    data: InputData,
    public option: InputHelperOption = {},
  ) {
    option.type = option.type ?? 'default';
    super(data);

    this.setValidator();
    this.validateValue();
    this.setEnable();
  }

  override template() {
    return html` <div ${this.makeInputHelperAttrHtml('data')} class="${klass.input_helper}">
      ${this.makeHelperText()}
    </div>`;
  }

  setValidator() {
    if (this.option.validators?.length === 1 && this.option.type !== 'check') {
      this.validator = this.option.validators[0];
    }
  }

  setEnable() {
    if (this.option.type === 'always') {
      this.state.enable = true;
      return;
    }

    if (this.validator && !this.state.valid) {
      this.state.enable = true;
      return;
    }

    if (this.option.validators && !this.validator) {
      this.state.enable = true;
      return;
    }

    this.state.enable = false;
  }

  validateValue() {
    if (!this.option.validators?.length) {
      this.state.valid = true;
      return;
    }

    pipe(
      this.option.validators ?? [],
      zipWithIndex,
      map(([index, { validator }]) => {
        return (this.validators_status[index] =
          (this.data.value || this.option.type === 'all') && validator ? validator(this.data.value ?? '') : null);
      }),
      filter((status) => status !== false),
      toArray,
      (status) => {
        this.state.valid =
          this.data.value || this.option.type === 'all' ? status.length === this.validators_status.length : true;
      },
    );
  }

  makeHelperText() {
    if (this.validator) {
      return new Typo({ text: this.validator.message }, { typo: 'p3' });
    }
    if (this.option.validators) {
      return html`
        ${this.option.validators.map(
          (validator, index) => html`
            <span class="${klass.text}" data-index="${index}" data-valid=${this.validators_status[index]}>
              ${new Icon({ name: 'check', size: 12 })} ${new Typo({ text: validator.message }, { typo: 'p3' })}
            </span>
          `,
        )}
      `;
    }
    return html``;
  }

  makeInputHelperAttr() {
    return pipe({ ...this.state, type: this.option.type }, entries);
  }

  makeInputHelperAttrHtml(prefix?: string) {
    return html`${pipe(
      this.makeInputHelperAttr(),
      map(([k, v]) => `${prefix ? prefix + '-' : ''}${k}=${v}`),
      join(' '),
    )}`;
  }

  override redraw() {
    this.setValidator();
    this.validateValue();
    this.setEnable();

    if (this.validator) {
      super.redraw();
    } else {
      const helper$ = this.element() as HTMLDivElement;

      pipe(
        this.makeInputHelperAttr(),
        each(([k, v]) => {
          helper$.dataset[k] = v?.toString();
        }),
      );

      $(helper$)
        .findAll('[data-index]')
        .map(($el, index) => {
          const el = $el.element();
          el.dataset.valid = this.validators_status[index]?.toString();
        });
    }

    return this;
  }
}

export class Input extends View<InputData> {
  private original_helper_option?: InputHelperOption;
  private has_helper_option_changed?: boolean;

  inputBaseView: InputBase;
  inputControllerView: InputController;
  inputHelperView: InputHelper;

  constructor(
    data: InputData,
    public option: InputOption = {},
  ) {
    super(data);

    this.option.attr = this.option.attr ?? {};

    this.data.id = this.data.id ?? this.option.attr.name ?? undefined;

    if (this.option.attr.type === 'number') {
      this.option.attr.inputmode = this.option.attr.inputmode ?? 'numeric';
      if (this.option.commify) {
        this.option.attr.type = 'text';
      }
    }

    this.inputBaseView = new InputBase(data, this.option.attr);

    if (this.option.attr.type === 'password') {
      this.option.controller = this.option.controller ?? {};
      this.option.controller.type = this.option.attr.type;
    }

    this.inputControllerView = new InputController(data, this.option.controller);

    if (this.option.controller?.type === 'timer') {
      this.option.helper = this.option.helper ?? {};
      this.option.helper.type = 'all';
      this.option.helper.validators = [
        this.inputControllerView.timerValidator(this.option.helper.validators?.[0]?.message),
      ];
    }

    this.inputHelperView = new InputHelper(data, this.option.helper);
  }

  override template() {
    return html`<div
      class="${klass.input_wrapper}${this.option.klass ? ' ' + this.option.klass : ''}"
      data-init="true"
      ${this.inputBaseView.makeInputBaseAttrHtml('data')}
      ${this.inputHelperView.makeInputHelperAttrHtml('data-helper')}
    >
      <div class="${klass.input}">${this.inputBaseView} ${this.inputControllerView}</div>
      ${this.inputHelperView}
    </div> `;
  }

  override onRender() {
    this.delegate('input', InputBase, this._changeHandler.bind(this));
    this.delegate('focusin', InputBase, this._focusToggleHandler(true));
    this.delegate('focusout', InputBase, this._focusToggleHandler(false));
    this.delegate('blur', InputBase, this._focusToggleHandler(false));

    if (this.option.controller?.type === 'timer') {
      this.addEventListener(InputTimerEndEvent, this._timerEndHandler);
      this.addEventListener(InputTimerStartEvent, this._timerStartHandler);
    } else {
      this.delegate('click', `.${this.inputControllerView.button_klass.reset}`, this._resetHandler);
      this.delegate('click', `.${this.inputControllerView.button_klass.visible}`, this._visibleToggleHandler);
    }

    const initial_value = this.data.value;
    if (initial_value !== this.inputBaseView.getValue()) {
      this.redraw();
    }
  }

  override redraw() {
    this.subViewsFromTemplate.forEach((view) => view.redraw());

    const input$ = this.element() as HTMLDivElement;

    pipe(
      this.inputBaseView.makeInputBaseAttr(),
      concat(this.inputHelperView.makeInputHelperAttr()),
      each(([k, v]) => {
        input$.dataset[k] = v?.toString();
      }),
    );

    if (input$.dataset.init === 'true') {
      input$.dataset.init = 'false';
    }

    input$.classList.remove(
      ...[...input$.classList.values()].filter(
        (_klass) => _klass !== this.toString() && _klass !== klass.input_wrapper,
      ),
    );
    if (this.option.klass) {
      this.option.klass.split(' ').forEach((k) => input$.classList.add(k));
    }

    return this;
  }

  private _changeHandler(_: Event, input_base: InputBase) {
    this.resetToOriginalHelper();

    let value = input_base.getValue();
    if (this.option.delete_pattern) {
      value = value.replace(this.option.delete_pattern, '');
    }

    if (this.option.attr?.type === 'number' && this.option.attr.min) {
      value = Number(value) < this.option.attr.min ? this.option.attr.min.toString() : value;
    }

    if (this.option.attr?.type === 'number' && this.option.attr.max) {
      value = Number(value) > this.option.attr.max ? this.option.attr.max.toString() : value;
    }

    if (this.option.commify && this.option.attr?.min) {
      value = Number(value.replace(/\D/g, '')) < this.option.attr.min ? this.option.attr.min.toString() : value;
    }

    if (this.option.commify && this.option.attr?.max) {
      value = Number(value.replace(/\D/g, '')) > this.option.attr.max ? this.option.attr.max.toString() : value;
    }

    if (this.option.commify) {
      value = commify(value.replace(/\D/g, ''));
    }

    input_base.setValue(value);
    this.data.value = this.option.commify ? value.replace(/,/g, '') : value;
    this.redraw();
    this.dispatchEvent(InputChangeEvent, { bubbles: true, detail: this });
  }

  private _resetHandler(_: Event) {
    this.inputBaseView.reset();
    this.data.value = this.inputBaseView.getValue();
    this.resetToOriginalHelper();
    this.redraw();
    this.dispatchEvent(InputChangeEvent, { bubbles: true, detail: this });
  }

  private _visibleToggleHandler(_: Event) {
    this.inputBaseView.option.type = this.inputControllerView.password_visible ? 'password' : 'text';
    this.inputControllerView.password_visible = !this.inputControllerView.password_visible;
    this.resetToOriginalHelper();
    this.redraw();
  }

  private _focusToggleHandler(focus: boolean) {
    const handler = (_e: Event, _input_base: InputBase) => {
      if (this.option.attr?.disabled || this.option.attr?.readonly) {
        this.element().dataset.focus = 'false';
        return;
      }

      this.element().dataset.focus = focus.toString();
    };

    return debounce(handler, 300);
  }

  private _timerEndHandler() {
    this.inputBaseView.option.disabled = true;
    this.resetToOriginalHelper();
    this.redraw();
  }

  private _timerStartHandler() {
    this.inputBaseView.option.disabled = false;
    this.resetToOriginalHelper();
    this.redraw();
  }

  private resetToOriginalHelper() {
    if (this.has_helper_option_changed) {
      this.has_helper_option_changed = false;
      this.inputHelperView.option.type = this.original_helper_option?.type;
      this.inputHelperView.option.validators = this.original_helper_option?.validators;
      this.original_helper_option = undefined;
    }
  }

  /**
   * @description api 통신을 통한 데이터 체크와 같은 로직 후에 실행해볼 수 있는 함수입니다.\
   * 에러 메시지와 조건을 설정해서 넘길 수 있습니다.\
   * 실행 즉시 redraw가 실행되며 포커스 이벤트 외 모든 이벤트에서 원래의 validator로 돌아갑니다.
   * */
  changeValidatorOnce(validator: InputValidatorOption, type?: InputHelperOption['type']) {
    if (!this.original_helper_option) {
      this.has_helper_option_changed = true;
      this.original_helper_option = { ...(this.inputHelperView.option ?? {}) };
    }

    this.inputHelperView.option.type = type ?? 'all';
    this.inputHelperView.option.validators = [validator];
    this.redraw();
  }

  resetValidator() {
    this.resetToOriginalHelper();
    this.redraw();
  }

  isValid() {
    return this.inputHelperView.state.valid;
  }

  blur() {
    this.inputBaseView.element().blur();
  }

  focus() {
    this.inputBaseView.element().focus();
  }

  disabled() {
    this.inputBaseView.option.disabled = true;
    this.blur();
    this.element().dataset.focus = 'false';
    this.redraw();
  }

  enabled() {
    this.inputBaseView.option.disabled = false;
    this.redraw();
  }

  readonly() {
    this.inputBaseView.option.readonly = true;
    this.blur();
    this.element().dataset.focus = 'false';
    this.redraw();
  }

  writable() {
    this.inputBaseView.option.readonly = false;
    this.redraw();
  }

  set value(value: string) {
    this.inputBaseView.setValue(value);
    this.dispatchEvent(InputChangeEvent, { bubbles: true, detail: this });
  }

  get value(): string {
    return this.inputBaseView.getValue() ?? '';
  }
}
